import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import {
  FiActivity,
  FiArrowRight,
  FiCheckCircle,
  FiCircle,
  FiClock,
  FiCloud,
  FiCode,
  FiDatabase,
  FiLayers,
  FiLink,
  FiSettings,
  FiShield,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';
import Header from './components/Header';
import Contact from './components/Contact';
import Footer from './components/Footer';
import PortfolioDetail from './components/PortfolioDetail';
import PortfolioIndex from './components/PortfolioIndex';
import Maintenance from './components/Maintenance';
import { AtelierConcept, CommonGroundConcept, ConceptGallery, FrameworkConcept, IndexConcept, OrbitConcept, SignalConcept } from './components/ConceptDemos';
import LogoConcepts from './components/LogoConcepts';
import StudioHome from './components/StudioHome';
import StudioPortfolioShell from './components/StudioPortfolioShell';


type HeroTabKey = 'overview' | 'architecture' | 'build' | 'integration' | 'quality';
type StatusTone = 'default' | 'done' | 'progress' | 'planned';

type SummaryCard = {
  label: string;
  value: string;
  icon: IconType;
};

type StatusItem = {
  text: string;
  tone?: StatusTone;
};

type HeroTab = {
  key: HeroTabKey;
  menu: string;
  eyebrow: string;
  title: string;
  description: string;
  summaryCards: SummaryCard[];
  leftTitle: string;
  leftItems: StatusItem[];
  rightTitle: string;
  rightItems: StatusItem[];
};

export const projectTabs: HeroTab[] = [
  {
    key: 'overview',
    menu: '프로젝트 개요',
    eyebrow: 'PROJECT OVERVIEW',
    title: '프로젝트 목표와 기술 범위를 하나의 기준으로 관리합니다.',
    description:
      '서비스 요구사항, 시스템 구조, 개발 일정과 품질 기준을 연결하여 기획부터 배포까지 일관된 방식으로 프로젝트를 수행합니다.',
    summaryCards: [
      { label: '프로젝트 유형', value: '신규 구축 · 시스템 개선', icon: FiLayers },
      { label: '수행 범위', value: '기획 · 디자인 · 개발 · 배포', icon: FiCode },
      { label: '기술 영역', value: 'Web · App · API · Cloud', icon: FiCloud },
    ],
    leftTitle: '수행 영역',
    leftItems: [
      { text: '서비스 및 업무 구조 분석' },
      { text: '시스템 아키텍처 설계' },
      { text: '웹·앱·관리자 개발' },
      { text: 'API 및 외부 서비스 연동' },
    ],
    rightTitle: '프로젝트 관리',
    rightItems: [
      { text: '요구사항 및 우선순위 관리', tone: 'done' },
      { text: '일정과 변경 영향 검토', tone: 'progress' },
      { text: '기능 및 품질 검수', tone: 'done' },
      { text: '배포 후 안정화 대응', tone: 'planned' },
    ],
  },
  {
    key: 'architecture',
    menu: '시스템 설계',
    eyebrow: 'SYSTEM ARCHITECTURE',
    title: '서비스 구조와 기술 레이어를 안정적으로 설계합니다.',
    description:
      '서비스 흐름, 데이터 구조, 권한 정책, 운영 환경까지 연결된 기준으로 설계를 정리해 구축 이후에도 흔들리지 않는 구조를 만듭니다.',
    summaryCards: [
      { label: '서비스 구조', value: '사용자 · 관리자 · 운영 흐름', icon: FiLayers },
      { label: '데이터 구조', value: '도메인 · 저장소 · 연계 데이터', icon: FiDatabase },
      { label: '기술 구성', value: 'Frontend · Backend · API · Infrastructure', icon: FiSettings },
    ],
    leftTitle: '설계 기준',
    leftItems: [
      { text: '서비스 구조' },
      { text: '데이터 구조' },
      { text: '권한 및 운영 정책 설계' },
      { text: '배포 환경 및 확장 기준 정리' },
    ],
    rightTitle: '기술 레이어',
    rightItems: [
      { text: 'Frontend · Backend · API · Infrastructure', tone: 'done' },
      { text: '화면 흐름과 도메인 구조 연결', tone: 'done' },
      { text: '운영 관점 예외 처리 설계', tone: 'progress' },
      { text: '배포 전 구성 검토', tone: 'planned' },
    ],
  },
  {
    key: 'build',
    menu: '구축 현황',
    eyebrow: 'BUILD STATUS',
    title: '구축 단계별 진행 상태를 명확하게 공유합니다.',
    description:
      '현재 어느 단계까지 완료되었는지와 다음 작업이 무엇인지 한 화면에서 확인할 수 있도록 구축 현황을 관리합니다.',
    summaryCards: [
      { label: '요구사항 분석', value: '완료', icon: FiCheckCircle },
      { label: 'UI/UX 설계', value: '완료', icon: FiCheckCircle },
      { label: '핵심 기능 개발', value: '진행 중', icon: FiClock },
    ],
    leftTitle: '현재 단계',
    leftItems: [
      { text: '요구사항 분석: 완료', tone: 'done' },
      { text: 'UI/UX 설계: 완료', tone: 'done' },
      { text: '핵심 기능 개발: 진행 중', tone: 'progress' },
      { text: '통합 테스트: 예정', tone: 'planned' },
    ],
    rightTitle: '다음 단계',
    rightItems: [
      { text: '운영 배포: 예정', tone: 'planned' },
      { text: '기능별 검수 일정 정리', tone: 'progress' },
      { text: '변경 요청 영향 검토', tone: 'progress' },
      { text: '배포 전 점검 항목 확정', tone: 'planned' },
    ],
  },
  {
    key: 'integration',
    menu: '연동 관리',
    eyebrow: 'INTEGRATION MANAGEMENT',
    title: '외부 서비스와 기존 시스템 연동 범위를 체계적으로 관리합니다.',
    description:
      '인증, 결제, 메시지, 지도, ERP, CMS, 기존 업무 시스템까지 필요한 연동 범위를 분리하고 기술 방식과 검증 기준을 함께 정리합니다.',
    summaryCards: [
      { label: '인증 및 회원', value: '권한 · 보안 · 계정 연동', icon: FiShield },
      { label: '외부 서비스', value: '결제 · 메시지 · 지도 등', icon: FiLink },
      { label: '기존 시스템', value: 'ERP · CMS · 업무 시스템', icon: FiDatabase },
    ],
    leftTitle: '연동 범위',
    leftItems: [
      { text: '인증 및 회원 시스템' },
      { text: '결제·메시지·지도 등 외부 서비스' },
      { text: 'ERP·CMS·기존 업무 시스템' },
      { text: '데이터 마이그레이션 및 API 연동' },
    ],
    rightTitle: '연동 관리 기준',
    rightItems: [
      { text: '연동 방식 및 책임 범위 정의', tone: 'done' },
      { text: '예외 케이스 및 실패 처리 정리', tone: 'progress' },
      { text: '테스트 시나리오 분리 검증', tone: 'progress' },
      { text: '운영 전환 및 데이터 반영 점검', tone: 'planned' },
    ],
  },
  {
    key: 'quality',
    menu: '품질 관리',
    eyebrow: 'QUALITY ASSURANCE',
    title: '배포 전후 품질 검증과 운영 안정화 기준을 확인합니다.',
    description:
      '기능 검수부터 브라우저·디바이스 호환성, 보안 점검, 운영 환경 검증까지 실제 서비스 오픈을 위한 준비 상태를 관리합니다.',
    summaryCards: [
      { label: '기능 검수', value: '핵심 플로우 점검', icon: FiCheckCircle },
      { label: '호환성 검증', value: '브라우저 · 디바이스 확인', icon: FiActivity },
      { label: '운영 검증', value: '배포 전 환경 점검', icon: FiShield },
    ],
    leftTitle: '점검 항목',
    leftItems: [
      { text: '기능 검수', tone: 'done' },
      { text: '브라우저·디바이스 호환성', tone: 'done' },
      { text: '보안 및 권한 점검', tone: 'progress' },
      { text: '배포 전 운영 환경 검증', tone: 'planned' },
    ],
    rightTitle: '운영 안정화',
    rightItems: [
      { text: '배포 체크리스트 확인', tone: 'done' },
      { text: '로그 및 모니터링 점검', tone: 'progress' },
      { text: '운영 이슈 대응 기준 정리', tone: 'progress' },
      { text: '오픈 후 안정화 대응', tone: 'planned' },
    ],
  },
];

export const processSteps = [
  '01 서비스 설계',
  '02 시스템 구축',
  '03 통합 및 검증',
  '04 배포 및 운영',
];

export const tabIcons: Record<HeroTabKey, IconType> = {
  overview: FiLayers,
  architecture: FiSettings,
  build: FiCode,
  integration: FiLink,
  quality: FiCheckCircle,
};

const statusStyles: Record<StatusTone, string> = {
  default: 'border-slate-200 bg-white text-slate-700',
  done: 'border-[#005322]/15 bg-[#005322]/[0.06] text-slate-800',
  progress: 'border-emerald-200 bg-emerald-50 text-slate-800',
  planned: 'border-slate-200 bg-slate-50 text-slate-700',
};

const badgeStyles: Record<StatusTone, string> = {
  default: 'bg-slate-100 text-slate-500',
  done: 'bg-[#005322]/10 text-[#005322]',
  progress: 'bg-emerald-100 text-emerald-700',
  planned: 'bg-slate-200 text-slate-600',
};

const StatusDot: React.FC<{ tone: StatusTone }> = ({ tone }) => {
  if (tone === 'done') {
    return <FiCheckCircle className="text-[#005322]" />;
  }
  if (tone === 'progress') {
    return <FiActivity className="text-emerald-600" />;
  }
  if (tone === 'planned') {
    return <FiClock className="text-slate-500" />;
  }
  return <FiCircle className="text-slate-400" />;
};

const StatusBadge: React.FC<{ tone: StatusTone }> = ({ tone }) => {
  const label =
    tone === 'done' ? '완료' : tone === 'progress' ? '점검 중' : tone === 'planned' ? '예정' : '관리';

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${badgeStyles[tone]}`}>
      {label}
    </span>
  );
};

export const SummaryCardView: React.FC<{ card: SummaryCard }> = ({ card }) => {
  const Icon = card.icon;

  return (
    <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-500 md:text-sm">
          {card.label}
        </p>
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#005322]">
          <Icon className="text-[15px]" />
        </span>
      </div>
      <p className="mt-3 text-sm font-semibold leading-6 text-slate-900 md:text-base">{card.value}</p>
    </div>
  );
};

export const ListPanel: React.FC<{
  title: string;
  icon: IconType;
  items: StatusItem[];
}> = ({ title, icon: Icon, items }) => (
  <div className="rounded-[20px] border border-slate-200 bg-slate-50/80 px-4 py-4 md:px-5 md:py-5">
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#005322]">
          <Icon className="text-[16px]" />
        </span>
        <h3 className="text-base font-semibold text-slate-950 md:text-lg">{title}</h3>
      </div>
      <StatusBadge tone="done" />
    </div>

    <div className="mt-4 space-y-2.5">
      {items.map((item) => {
        const tone = item.tone ?? 'default';

        return (
          <div
            key={item.text}
            className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm leading-6 ${statusStyles[tone]}`}
          >
            <div className="flex items-center gap-3">
              <StatusDot tone={tone} />
              <span>{item.text}</span>
            </div>
            <StatusBadge tone={tone} />
          </div>
        );
      })}
    </div>
  </div>
);

export const SideNavButton: React.FC<{
  label: string;
  icon: IconType;
  active: boolean;
  onClick: () => void;
}> = ({ label, icon: Icon, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex shrink-0 items-center justify-between gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium transition md:w-full ${
      active ? 'bg-white text-[#005322] shadow-sm' : 'text-white/82 hover:bg-white/10 hover:text-white'
    }`}
  >
    <span className="flex items-center gap-2.5">
      <Icon className={`text-[15px] ${active ? 'text-[#005322]' : 'text-white/70'}`} />
      <span>{label}</span>
    </span>
    {active && <span className="h-2.5 w-2.5 rounded-full bg-[#005322]" />}
  </button>
);

const MainHero: React.FC = () => (
  <section id="about" className="relative isolate min-h-[690px] overflow-hidden bg-[#171713] text-white md:min-h-[760px]">
    <img src="/images/editorial/studio-hero-v2.png" alt="솜니움 라이브의 디지털 스튜디오" className="absolute inset-0 h-full w-full object-cover object-[62%_center]" />
    <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(13,14,12,0.96)_0%,rgba(13,14,12,0.72)_44%,rgba(13,14,12,0.12)_100%)]" />
    <div className="relative mx-auto flex min-h-[690px] max-w-[1200px] flex-col justify-between px-5 py-12 md:min-h-[760px] md:px-8 md:py-16">
      <p className="text-xs font-bold tracking-[0.26em] text-[#d8ddbf]">SOMNIUM LIVE / DIGITAL STUDIO</p>
      <div className="max-w-[720px] pb-6">
        <p className="mb-5 text-sm font-medium text-white/65">WEBSITE · APP · BUSINESS SYSTEM</p>
        <h1 className="break-keep text-[48px] font-bold leading-[1.03] tracking-[-0.075em] md:text-[78px] lg:text-[92px]">생각을 설계하고,<br /><span className="text-[#d8ddbf]">경험을 완성합니다.</span></h1>
        <p className="mt-7 max-w-[520px] break-keep text-base leading-8 text-white/70 md:text-lg">아이디어가 선명한 서비스가 되기까지. 기획과 디자인, 개발의 모든 과정을 한 흐름으로 연결합니다.</p>
        <div className="mt-9 flex flex-col gap-3 sm:flex-row"><a href="#contact" className="inline-flex h-14 items-center justify-center gap-2 bg-[#d8ddbf] px-8 font-bold text-[#181914] transition hover:bg-white">프로젝트 이야기하기 <FiArrowRight /></a><a href="#work" className="inline-flex h-14 items-center justify-center border border-white/40 px-8 font-semibold text-white transition hover:bg-white/10">작업 보기</a></div>
      </div>
      <div className="flex items-center justify-between border-t border-white/25 pt-5 text-xs font-semibold tracking-[0.13em] text-white/60"><span>SCROLL TO EXPLORE</span><span>01 — 04</span></div>
    </div>
  </section>
);

const StudioIntro: React.FC = () => (
  <section className="bg-[#e9e7df] px-5 py-20 text-[#151613] md:px-8 md:py-28">
    <div className="mx-auto grid max-w-[1200px] gap-12 lg:grid-cols-[0.65fr_1.35fr] lg:gap-24">
      <div><p className="text-xs font-bold tracking-[0.22em] text-[#526447]">WHAT WE DO</p><p className="mt-5 text-xl font-semibold leading-8">필요한 것을 함께 발견하고, 작동하는 방식으로 만드는 팀입니다.</p></div>
      <div><h2 className="break-keep text-[38px] font-bold leading-[1.15] tracking-[-0.06em] md:text-[58px]">보기 좋은 화면에서 끝나지 않고,<br />사업의 다음 흐름까지 만듭니다.</h2><div className="mt-12 grid gap-0 border-t border-[#151613]/25 sm:grid-cols-3">{[['01','전략과 구조'],['02','디자인과 개발'],['03','검수와 운영']].map(([number, label]) => <div key={number} className="border-b border-[#151613]/25 py-5 sm:border-r sm:px-5 sm:first:pl-0"><p className="text-xs font-bold text-[#526447]">{number}</p><p className="mt-3 font-bold">{label}</p></div>)}</div></div>
    </div>
  </section>
);

const SelectedWork: React.FC = () => (
  <section id="work" className="bg-[#171713] px-5 py-20 text-white md:px-8 md:py-28">
    <div className="mx-auto max-w-[1200px]"><div className="flex items-end justify-between gap-6"><div><p className="text-xs font-bold tracking-[0.22em] text-[#d8ddbf]">SELECTED WORK</p><h2 className="mt-4 text-4xl font-bold tracking-[-0.05em] md:text-5xl">최근의 작업들</h2></div><a href="/portfolio" className="hidden text-sm font-bold underline underline-offset-4 md:block">전체 프로젝트 보기</a></div><div className="mt-12 grid gap-6 md:grid-cols-2"><a href="/portfolio/enterprise-service-landing-renewal" className="group block"><div className="overflow-hidden bg-[#292a25]"><img src="/images/portfolio/enterprise-service-landing-renewal.png" alt="기업 주요 서비스 랜딩페이지 리뉴얼" className="aspect-[1.18/1] w-full object-cover transition duration-500 group-hover:scale-105" /></div><div className="mt-5 flex items-start justify-between gap-5"><div><p className="text-xs font-bold tracking-[0.12em] text-[#d8ddbf]">WEB · UX/UI · PUBLISHING</p><h3 className="mt-2 text-xl font-bold">기업 주요 서비스 랜딩페이지 리뉴얼</h3></div><FiArrowRight className="mt-1 shrink-0 text-xl" /></div></a><a href="/portfolio/ai-based-diagnostic-service" className="group block md:mt-20"><div className="overflow-hidden bg-[#292a25]"><img src="/images/portfolio/ai-based-diagnostic-service.png" alt="AI 기반 맞춤형 진단 서비스" className="aspect-[1.18/1] w-full object-cover transition duration-500 group-hover:scale-105" /></div><div className="mt-5 flex items-start justify-between gap-5"><div><p className="text-xs font-bold tracking-[0.12em] text-[#d8ddbf]">SERVICE PLANNING · UX/UI</p><h3 className="mt-2 text-xl font-bold">AI 기반 맞춤형 진단 서비스</h3></div><FiArrowRight className="mt-1 shrink-0 text-xl" /></div></a></div><a href="/portfolio" className="mt-10 inline-block text-sm font-bold underline underline-offset-4 md:hidden">전체 프로젝트 보기</a></div>
  </section>
);

const SiteLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const { pathname, hash } = useLocation();

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (pathname === '/' && hash) {
      const targetId = hash.slice(1);
      const frame = window.requestAnimationFrame(() => {
        document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return () => window.cancelAnimationFrame(frame);
    }

    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0 }));
    return () => window.cancelAnimationFrame(frame);
  }, [hash, pathname]);

  return (
    <div ref={topRef} className="min-h-screen bg-white">
      <Header
        isMenuOpen={menuOpen}
        onMenuToggle={() => setMenuOpen(!menuOpen)}
        onScrollToTop={() => {
          topRef.current?.scrollIntoView({ behavior: 'smooth' });
        }}
        onProjectInquiry={() => {
          if (pathname === '/') {
            scrollToContact();
          }
        }}
      />
      {children}
      <Footer
        onScrollToTop={() => {
          topRef.current?.scrollIntoView({ behavior: 'smooth' });
        }}
      />
    </div>
  );
};

const HomePage: React.FC = () => (
  <SiteLayout>
    <MainHero />
    <StudioIntro />
    <SelectedWork />
    <Contact />
  </SiteLayout>
);

const PortfolioPage: React.FC = () => (
  <StudioPortfolioShell>
    <PortfolioIndex />
  </StudioPortfolioShell>
);

const PortfolioDetailPage: React.FC = () => (
  <StudioPortfolioShell>
    <PortfolioDetail />
  </StudioPortfolioShell>
);

const MaintenancePage: React.FC = () => (
  <SiteLayout>
    <Maintenance />
  </SiteLayout>
);

const App: React.FC = () => (
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<StudioHome />} />
      <Route path="/concepts" element={<ConceptGallery />} />
      <Route path="/concept/atelier" element={<AtelierConcept />} />
      <Route path="/concept/signal" element={<SignalConcept />} />
      <Route path="/concept/common-ground" element={<CommonGroundConcept />} />
      <Route path="/concept/framework" element={<FrameworkConcept />} />
      <Route path="/concept/orbit" element={<OrbitConcept />} />
      <Route path="/concept/index" element={<IndexConcept />} />
      <Route path="/logo-concepts" element={<LogoConcepts />} />
      <Route path="/home-demo" element={<HomePage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
      <Route path="/portfolio/:slug" element={<PortfolioDetailPage />} />
      <Route path="/maintenance" element={<MaintenancePage />} />
      <Route path="*" element={<ConceptGallery />} />
    </Routes>
  </BrowserRouter>
);

export default App;
